import type { TResponse } from '@/services';
import { ApiError, apis } from '@/libs-service';
import { UPZI_USER_ID } from '@/common';
import type {
  AskApplicationResultResponse,
  ApplicationDetailResponse,
  ApplicationFeedbackStatusesResponse,
  ApplicationResultFeedbackRequest,
  AppliedJobsQuery,
  AppliedJobsResponse,
  AskApplicationResultRequest,
} from './model';

export type {
  ApplicationAskResult,
  ApplicationDetailData,
  ApplicationDetailResponse,
  ApplicationFeedbackStatus,
  ApplicationProgressStatus,
  ApplicationResultFeedbackRequest,
  AppliedJob,
  AppliedJobsPage,
  AppliedJobsQuery,
  AskApplicationResultRequest,
} from './model';

const APPLY_JOB_API_ENDPOINT = 'application/external/api/v1/apply';
const CHECK_APPLICATION_EXIST_API_ENDPOINT =
  'application/external/api/v1/check-application-exist';
const APPLIED_JOBS_API_ENDPOINT =
  'jobseekers-info/external/api/v1/application/applied-jobs';
const APPLICATION_DETAIL_API_ENDPOINT =
  'jobseekers-info/external/api/v1/application/detail';
const APPLICATION_RESULT_ASK_API_ENDPOINT =
  'jobseekers-info/external/api/v1/application/result/ask';
const APPLICATION_RESULT_FEEDBACK_API_ENDPOINT =
  'jobseekers-info/external/api/v1/application/result/feedback';
const APPLICATION_RESULT_FEEDBACK_STATUS_API_ENDPOINT =
  'jobseekers-info/external/api/v1/application/result/feedback-status';

export type ApplyJobAttachment = {
  extension: string;
  fileMime: string;
  fileName: string;
  filePath: string;
  fileType: number;
};

export type ApplyJobRequest = {
  attachment: ApplyJobAttachment[];
  jobId: string;
  sourceType: number;
};

export type CheckApplicationExistData = {
  isApplied: boolean;
};

function readBrowserCookie(key: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');

    if (eq === -1) {
      continue;
    }

    const cookieKey = trimmed.slice(0, eq);

    if (cookieKey !== key) {
      continue;
    }

    return decodeURIComponent(trimmed.slice(eq + 1));
  }

  return undefined;
}

export async function applyJob(data: ApplyJobRequest): Promise<TResponse> {
  try {
    const response = await apis.publicApi.post<TResponse, ApplyJobRequest>(
      `/${APPLY_JOB_API_ENDPOINT}`,
      data
    );

    return response;
  } catch (e) {
    if (e instanceof ApiError) {
      console.error(e.status, e.body);

      if (isResponseBody(e.body)) {
        return e.body;
      }
    } else {
      console.error(e);
    }
  }

  return {
    status: 'error',
    message: 'Network or unexpected error',
    data: null,
  };
}

export async function checkApplicationExist(
  jobId: string,
  signal?: AbortSignal
): Promise<TResponse<CheckApplicationExistData>> {
  try {
    const response = await apis.publicApi.get<
      TResponse<CheckApplicationExistData>
    >(`/${CHECK_APPLICATION_EXIST_API_ENDPOINT}`, { jobId }, { signal });

    return response;
  } catch (e) {
    if (signal?.aborted || (e instanceof Error && e.name === 'AbortError')) {
      return {
        status: 'error',
        message: 'Request cancelled',
        data: {
          isApplied: false,
        },
      };
    }

    if (e instanceof ApiError) {
      console.error(e.status, e.body);

      if (isCheckApplicationExistResponseBody(e.body)) {
        return e.body;
      }
    } else {
      console.error(e);
    }
  }

  return {
    status: 'error',
    message: 'Network or unexpected error',
    data: {
      isApplied: false,
    },
  };
}

export function getAppliedJobs(
  query: AppliedJobsQuery = {}
): Promise<AppliedJobsResponse> {
  return apis.publicApi.get<AppliedJobsResponse>(
    `/${APPLIED_JOBS_API_ENDPOINT}`,
    { limit: 10, page: 1, ...query }
  );
}

export function getApplicationDetail(
  applicationId: string
): Promise<ApplicationDetailResponse> {
  return apis.publicApi.get<ApplicationDetailResponse>(
    `/${APPLICATION_DETAIL_API_ENDPOINT}/${encodeURIComponent(applicationId)}`
  );
}

export function getApplicationFeedbackStatuses(): Promise<ApplicationFeedbackStatusesResponse> {
  return apis.publicApi.get<ApplicationFeedbackStatusesResponse>(
    `/${APPLICATION_RESULT_FEEDBACK_STATUS_API_ENDPOINT}`
  );
}

export function askApplicationResult(
  data: AskApplicationResultRequest
): Promise<AskApplicationResultResponse> {
  return apis.publicApi.post<
    AskApplicationResultResponse,
    AskApplicationResultRequest
  >(`/${APPLICATION_RESULT_ASK_API_ENDPOINT}`, data);
}

export function submitApplicationResultFeedback(
  data: ApplicationResultFeedbackRequest
): Promise<TResponse> {
  return apis.publicApi.post<TResponse, ApplicationResultFeedbackRequest>(
    `/${APPLICATION_RESULT_FEEDBACK_API_ENDPOINT}`,
    data
  );
}

function isResponseBody(value: unknown): value is TResponse {
  return value !== null && typeof value === 'object' && 'status' in value;
}

function isCheckApplicationExistResponseBody(
  value: unknown
): value is TResponse<CheckApplicationExistData> {
  return value !== null && typeof value === 'object' && 'status' in value;
}
